import type { TFunction } from "i18next";

import i18n from "@/i18n";
import type { IssuedAssetFile } from "@/lib/api/client";
import type {
  AssetMediaRef,
  AssetMentionAliasInput,
  AssetMentionBindingTable,
  AssetMentionRef,
  ChoiceOption,
  ConceptAsset,
  BillingPeriod,
  ProductAssetPrice,
  QuestionDefinition,
  TestedAsset,
  TestedAssetBlock,
  TestedAssetType,
} from "@digital-twins/shared-types";
import type { AssetTextProvenance } from "@digital-twins/shared-types";
import {
  ASSET_AUTHORED_METADATA_KEY,
  ASSET_MENTION_TRAILING_LOOKAHEAD,
  FILES_PER_ASSET,
  assetFiles,
  assetTextReachesRespondents,
  isAssetDescriptionPlaceholder,
  isAssetTextProvenance,
  readAssetTextProvenance,
  assetMentionAliasPattern,
  buildAssetMentionAliases,
  buildAssetMentionBindingTable,
  canonicalYoutubeWatchUrl,
  directVideoMimeType,
  isDirectVideoUrl,
  mintKey,
  nonKeyframeableVideoMessage,
  productPriceStructureIssues,
  takenKeys,
} from "@digital-twins/shared-types";

function nowIso() {
  return new Date().toISOString();
}

export { buildAssetMentionAliases };

/** Mirrors the ordinal parsing inside shared-types `buildAssetMentionAliases`. */
function ordinalFromStableAssetKey(value: string | undefined) {
  const normalized = (value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const match = normalized.match(/(?:^|_)(ad|landing_page)_?(\d+)$/);
  return match ? { kind: match[1], index: match[2] } : null;
}

function resolveAssetMentionOrdinal(asset: AssetMentionAliasInput) {
  const stableCandidates = [
    asset.stableKey,
    asset.id,
    asset.id?.replace(/^asset_/, ""),
  ];
  return stableCandidates
    .map((candidate) => ordinalFromStableAssetKey(candidate))
    .find(Boolean);
}

/**
 * Questionnaire-builder chip / insert label for an @mention.
 *
 * The purple chip follows the asset's current title, same as matrix rows that
 * bind live to `concept.name`. `@Ad1` / `@Landing Page 2` remain aliases so
 * older copy still highlights.
 */
export function getQuestionnaireMentionDisplayLabel(
  asset: AssetMentionAliasInput,
): string {
  const named = asset.name.trim();
  if (named) {
    return named;
  }
  const type = String(asset.type);
  const ordinal = resolveAssetMentionOrdinal(asset);
  if ((ordinal?.kind === "ad" || type === "ad") && ordinal?.index) {
    return `Ad${ordinal.index}`;
  }
  if (
    (ordinal?.kind === "landing_page" || type === "landing_page") &&
    ordinal?.index
  ) {
    return `Landing Page ${ordinal.index}`;
  }
  return named;
}

export function getConceptAssets(
  testedAssets: TestedAssetBlock | null | undefined,
): Array<ConceptAsset> {
  return (testedAssets?.assets ?? []) as Array<ConceptAsset>;
}

/**
 * The tables a mention field binds by (C23-f), the Save's two passes: the
 * saved draft's catalog first, then the pending one. Built once per catalog
 * (`useQuestionnaireWorkspace`) and handed to every field.
 */
export interface AssetMentionTables {
  saved: AssetMentionBindingTable;
  live: AssetMentionBindingTable;
}

export function assetMentionTablesFor(
  saved: ReadonlyArray<TestedAsset>,
  live: ReadonlyArray<TestedAsset>,
): AssetMentionTables {
  return {
    saved: buildAssetMentionBindingTable({ assets: [...saved] }),
    live: buildAssetMentionBindingTable({ assets: [...live] }),
  };
}

/**
 * A tested-asset family's nouns, from its per-family copy
 * (`questionnaire.assetTypes.<type>`): the asset panel's heading and the
 * exposure strips name assets by them.
 */
export function assetTypeNouns(
  t: TFunction<"app">,
  type: TestedAssetType,
): { singular: string; plural: string } {
  return {
    plural: t(`questionnaire.assetTypes.${type}.plural`),
    singular: t(`questionnaire.assetTypes.${type}.singular`),
  };
}

/** The letter each family's badge shows in the asset panel. */
export const ASSET_TYPE_BADGE: Record<TestedAssetType, string> = {
  concept: "C",
  ad: "A",
  landing_page: "L",
  product: "P",
  packaging: "K",
  message: "M",
  brand: "B",
  pain: "P",
};

/** A family's nouns and badge, as the asset panel names it. */
export function assetTypeCopy(t: TFunction<"app">, type: TestedAssetType) {
  return { ...assetTypeNouns(t, type), badge: ASSET_TYPE_BADGE[type] };
}

/**
 * The panel's heading and nouns: the one owner, read by the panel, by the
 * chat when it points the user here (F16), and by the QA harness that records
 * what the builder shows.
 */
export function assetPanelLabel(
  concepts: ReadonlyArray<{ type: TestedAssetType }>,
  declaredAssetTypes: ReadonlyArray<TestedAssetType> = [],
  t: TFunction<"app">,
) {
  const fallback = {
    plural: t("questionnaire.assetsFallback.plural"),
    singular: t("questionnaire.assetsFallback.singular"),
    badge: "A",
  };
  if (concepts.length === 0) {
    // No assets yet: a single declared type (from study setup) still names
    // the panel, so "Add the first landing page" reads correctly.
    return declaredAssetTypes.length === 1
      ? assetTypeCopy(t, declaredAssetTypes[0])
      : fallback;
  }
  const types = new Set(concepts.map((c) => c.type));
  if (types.size === 1) return assetTypeCopy(t, [...types][0] as TestedAssetType);
  // Several types the study declares (an ad tile beside its messages) are a
  // legitimate mix: "Assets". A type the study does not declare is a state
  // nothing downstream handles, and exactly when the author needs to see the
  // type control: naming it turns the header into the one-click fix (picking
  // a type re-types every asset). MT-22: an old not-done line was redrawn as
  // "Mixed types panel" for a declared mix.
  if ([...types].every((type) => declaredAssetTypes.includes(type))) return fallback;
  return {
    plural: t("questionnaire.assetsMixed.plural"),
    singular: t("questionnaire.assetsMixed.singular"),
    badge: "A",
  };
}

/**
 * The one asset type this study is testing, or null when it has no assets yet
 * or (a state nothing downstream expects) more than one type.
 *
 * A study tests one kind of thing: every type-specific path — the landing-page
 * composites and deck, the ad monadic lanes, the per-type asset fields — is
 * gated on every asset sharing a type, and a mixed set silently falls off all
 * of them. This is the value new assets inherit so a mix is not created in the
 * first place, and what the assets panel shows the author.
 */
export function studyAssetType(
  testedAssets: TestedAssetBlock | null | undefined,
): TestedAssetType | null {
  const types = new Set(
    getConceptAssets(testedAssets)
      .map((concept) => concept.type)
      .filter(Boolean),
  );
  return types.size === 1 ? ([...types][0] as TestedAssetType) : null;
}

// The provenance reader and the respondent rule are shared-types', the one
// owner the compiler's respondent projection reads too.
export {
  assetTextReachesRespondents,
  isAssetDescriptionPlaceholder,
  readAssetTextProvenance,
};
export type { AssetTextProvenance };

/**
 * Merge provenance into an asset's metadata, returning a NEW metadata object.
 * Mirrors the description verdict onto the legacy `descriptionIsPlaceholder`
 * boolean for consumers not yet migrated. That mirror is derived here and must
 * never be written by hand anywhere else.
 */
export function withAssetTextProvenance(
  metadata: Record<string, unknown> | undefined,
  patch: Partial<Record<"name" | "description", AssetTextProvenance>>,
): Record<string, unknown> {
  const base = metadata ?? {};
  const prior = (base[ASSET_AUTHORED_METADATA_KEY] ?? {}) as Record<
    string,
    unknown
  >;
  const authored: Record<string, unknown> = { ...prior };
  for (const field of ["name", "description"] as const) {
    const value = patch[field];
    if (value) authored[field] = value;
  }
  const next: Record<string, unknown> = {
    ...base,
    [ASSET_AUTHORED_METADATA_KEY]: authored,
  };
  if (isAssetTextProvenance(authored.description)) {
    next.descriptionIsPlaceholder = authored.description === "placeholder";
  }
  return next;
}

/**
 * Title write for a message asset. D4: a message title is a label, not the
 * stimulus, so Title writes `name` only. The body — `messageText` and
 * `description`, mirrored — is written separately by `patchFromMessageBody`.
 */
export function patchFromMessageTitle(
  nextName: string,
  metadata: Record<string, unknown> | undefined,
): Record<string, unknown> {
  return {
    name: nextName,
    metadata: withAssetTextProvenance(metadata, {
      name: "human",
    }),
  };
}

/**
 * Body write for a message asset. The body IS the stimulus, so it writes
 * `messageText` and `description` together (mirrored), which keeps the
 * unrendered required-field check on `description` satisfied without a
 * separate description box.
 */
export function patchFromMessageBody(
  nextText: string,
  metadata: Record<string, unknown> | undefined,
): Record<string, unknown> {
  return {
    messageText: nextText,
    description: nextText,
    metadata: withAssetTextProvenance(metadata, {
      description: "human",
    }),
  };
}

/** A product card's typed price, when it has one (cards are typed loosely). */
export function productPriceOf(
  concept: ConceptAsset,
): ProductAssetPrice | undefined {
  return (concept as unknown as { price?: ProductAssetPrice }).price;
}

/**
 * The product card's price write, from its amount, currency and billing
 * period fields. Amount and currency both empty is no price (the Save removes
 * a stored one), and the period goes with it; anything else is written as
 * typed, the period only when one is chosen (absent is no period, decision
 * 28), and the price contract says whether it is complete.
 */
export function patchFromProductPrice(
  amount: number,
  currencyCode: string,
  billingPeriod?: BillingPeriod,
): Record<string, unknown> {
  return {
    price:
      Number.isNaN(amount) && !currencyCode.trim()
        ? undefined
        : { amount, currencyCode, ...(billingPeriod ? { billingPeriod } : {}) },
  };
}

/**
 * Why a product card's price cannot be saved, in the user's language, or null:
 * the tested-asset price contract refuses it (`productPriceStructureIssues`,
 * the one owner of a valid price: an amount above 0 in one of
 * `PRODUCT_PRICE_CURRENCIES`, F18 cross review). No price at all is fine:
 * emptying both fields removes it.
 */
export function productPriceIssue(concept: ConceptAsset): string | null {
  const price = productPriceOf(concept);
  if (price === undefined) return null;
  return productPriceStructureIssues(price).length > 0
    ? i18n.t("questionnaire.priceInvalid", { ns: "app" })
    : null;
}

/**
 * Whether a machine may write this field. Anything a person typed is off
 * limits; explicit generated and placeholder text is fair game for a read that
 * actually watched the creative.
 *
 * With no provenance recorded the answer is deliberately conservative: an empty
 * box is free, but existing text is treated as the marketer's. Unrecorded means
 * either an asset stored before provenance existed or a producer that does not
 * stamp, and overwriting someone's words is the worse failure of the two.
 */
export function assetTextIsMachineWritable(
  concept: Pick<ConceptAsset, "name" | "description" | "metadata"> | null | undefined,
  field: "name" | "description",
): boolean {
  const provenance = readAssetTextProvenance(concept, field);
  if (provenance) return provenance !== "human";
  const text = field === "name" ? concept?.name : concept?.description;
  if (!text?.trim()) return true;
  // Nothing recorded. A legacy description may still carry the old typed
  // boolean. An unrecorded name is deliberately preserved: its wording is not
  // evidence of who wrote it, and a user can legitimately call an asset "Ad 1".
  return field === "description"
    ? isAssetDescriptionPlaceholder(concept)
    : false;
}

export function ensureConceptAssetBlock(
  testedAssets: TestedAssetBlock,
): TestedAssetBlock {
  // Declared types = whatever was declared plus the actual types of the
  // assets present (matches backend normalizeTestedAssetsBlock).
  const derivedTypes = Array.from(
    new Set([
      ...testedAssets.assetTypes,
      ...(testedAssets.assets ?? [])
        .map((asset) => (asset as ConceptAsset).type)
        .filter(Boolean),
    ]),
  ) as Array<TestedAssetType>;
  const hasAssets = (testedAssets.assets ?? []).length > 0;
  return {
    ...testedAssets,
    // Backend-parity shape: assets present or a required-by-design block ⇒
    // all_assets; otherwise back to "none" so the empty-state picker returns
    // after the last asset is removed. `required` belongs to the STUDY design
    // (setup/goal/chat flows set it) — a manual optional add never forces it,
    // which also keeps the backend's required-only description gate from
    // rejecting in-progress concept/pain assets on every save (RND-2680).
    mode:
      hasAssets || testedAssets.required === true ? "all_assets" : "none",
    required: testedAssets.required === true,
    assetTypes: derivedTypes,
    validation: testedAssets.validation ?? {
      status: "not_validated",
      issues: [],
    },
  };
}

// Human name for an asset type: "landing_page" -> "Landing page".
function assetTypeDisplayName(type: TestedAssetType) {
  const spaced = String(type).replace(/_/g, " ");
  return `${spaced.charAt(0).toUpperCase()}${spaced.slice(1)}`;
}

// Per-type starter fields: the typed fields a new asset of the family must
// carry (`TestedAsset` in shared-types), for the panel's "Add"
// (`createConceptAsset`). A collected one gets the server's
// (`common/collected-assets`).
export function assetTypeTemplateFields(
  type: TestedAssetType,
): Record<string, unknown> {
  if (type === "message") {
    // Blank, with the card's placeholder: seeded words ("Message text") would
    // reach twins as the claim once no client rule refuses them (QA4 FE3).
    return { messageText: "" };
  }
  return {};
}

export function createConceptAsset(
  testedAssets: TestedAssetBlock,
  index: number,
  explicitType?: TestedAssetType,
): ConceptAsset {
  const existing = takenKeys(
    (testedAssets.assets ?? []).map((asset) => asset.stableKey),
  );

  // Explicit type (manual "Add an asset" picker, RND-2680) wins; otherwise
  // inherit the type from existing assets so the label stays consistent
  // (e.g. "brand" stays "brand" when the user adds more), falling back to the
  // single declared type before defaulting to "concept".
  const existingTypes = new Set((testedAssets.assets ?? []).map((a) => a.type))
  const declaredTypes = testedAssets.assetTypes ?? [];
  const inferredType: TestedAssetType =
    explicitType ??
    (existingTypes.size === 1
      ? [...existingTypes][0]
      : existingTypes.size === 0 && declaredTypes.length === 1
        ? declaredTypes[0]
        : "concept");

  const stableKey = mintKey([`${inferredType}_${index + 1}`], existing, "asset");
  const now = nowIso();
  return {
    id: `asset_${stableKey}`,
    // The builder carries every asset family through the `ConceptAsset`
    // shape; the family itself is `inferredType`.
    type: inferredType as ConceptAsset["type"],
    stableKey,
    name: `${assetTypeDisplayName(inferredType)} ${index + 1}`,
    description: "",
    media: [],
    // Nobody has named this yet: it is a sequential stand-in, so an automatic
    // read is free to replace it.
    metadata: withAssetTextProvenance(
      {},
      {
        name: "placeholder",
        ...(inferredType === "message" ? { description: "placeholder" as const } : {}),
      },
    ),
    ...assetTypeTemplateFields(inferredType),
    createdAt: now,
    updatedAt: now,
  };
}

export function upsertConceptAsset(
  testedAssets: TestedAssetBlock,
  conceptId: string,
  patch: Partial<ConceptAsset>,
): TestedAssetBlock {
  const nextAssets = testedAssets.assets.map((asset) =>
    asset.id === conceptId
      ? {
          ...asset,
          ...patch,
          stableKey: asset.stableKey,
          updatedAt: nowIso(),
        }
      : asset,
  );
  return ensureConceptAssetBlock({ ...testedAssets, assets: nextAssets });
}

export function addConceptAsset(
  testedAssets: TestedAssetBlock,
  explicitType?: TestedAssetType,
): TestedAssetBlock {
  const concepts = getConceptAssets(testedAssets);
  return ensureConceptAssetBlock({
    ...testedAssets,
    assets: [
      ...testedAssets.assets,
      createConceptAsset(testedAssets, concepts.length, explicitType),
    ],
  });
}

export function removeConceptAsset(
  testedAssets: TestedAssetBlock,
  conceptId: string,
): TestedAssetBlock {
  return ensureConceptAssetBlock({
    ...testedAssets,
    assets: testedAssets.assets.filter((asset) => asset.id !== conceptId),
  });
}

/**
 * A file put on an asset in the panel: it comes first, and the asset keeps
 * at most the family registry's `filesPerAsset` files (`FILES_PER_ASSET`,
 * the applier's `asset_files_over_limit`), each with its slices. With one,
 * a new picture, video, document or page replaces the file the asset had,
 * whatever its kind: a re-upload never stacks a second file.
 */
export function attachConceptImage(
  testedAssets: TestedAssetBlock,
  conceptId: string,
  mediaRef: AssetMediaRef,
): TestedAssetBlock {
  const existing =
    getConceptAssets(testedAssets).find((concept) => concept.id === conceptId)
      ?.media ?? [];
  const kept = new Set(
    assetFiles(existing)
      .slice(0, FILES_PER_ASSET - 1)
      .map((media) => media.id),
  );
  const survivors = existing.filter((media) =>
    kept.has(media.sliceOf ?? media.id),
  );
  return upsertConceptAsset(testedAssets, conceptId, {
    media: [mediaRef, ...survivors],
  });
}

export function mediaRefFromYoutubeUrl(url: string): AssetMediaRef | null {
  const canonical = canonicalYoutubeWatchUrl(url);
  if (!canonical) {
    return null;
  }
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? `media_yt_${crypto.randomUUID()}`
      : `media_yt_${Date.now()}`;
  return {
    id,
    kind: "video",
    url: canonical,
    publicUrl: canonical,
    mimeType: "video/youtube",
    label: "YouTube video",
    altText: "YouTube video",
  };
}

/**
 * A URL that points straight at video bytes attaches as the video itself, not
 * as a screenshot of whatever page happens to serve it. Null for anything else,
 * including the hosts `unreadableVideoHost` names: those serve an HTML player
 * page, so treating them as a file would hand the reader a web page.
 */
export function mediaRefFromVideoUrl(url: string): AssetMediaRef | null {
  const trimmed = (url || "").trim();
  if (!isDirectVideoUrl(trimmed)) {
    return null;
  }
  const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? `media_vid_${crypto.randomUUID()}`
      : `media_vid_${Date.now()}`;
  return {
    id,
    kind: "video",
    url: normalized,
    publicUrl: normalized,
    mimeType: directVideoMimeType(normalized) ?? "video/mp4",
    label: "Video",
    altText: "Video",
  };
}

export function attachConceptUrl(
  testedAssets: TestedAssetBlock,
  conceptId: string,
  url: string,
): TestedAssetBlock {
  const blocked = nonKeyframeableVideoMessage(url);
  if (blocked) {
    throw new Error(blocked);
  }
  // A video link is the video itself; any other page is captured on Save
  // from a bare `url_snapshot`. Either is the asset's file, by the one rule.
  return attachConceptImage(
    testedAssets,
    conceptId,
    mediaRefFromVideoUrl(url) ??
      ({
        id:
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? `url_${crypto.randomUUID()}`
            : `url_${Date.now()}`,
        kind: "url_snapshot",
        url: url.trim(),
      } as AssetMediaRef),
  );
}

export function assetRefForConcept(
  concept: ConceptAsset,
  role: AssetMentionRef["role"] = "stimulus",
  source: AssetMentionRef["source"] = "mention",
): AssetMentionRef {
  return {
    assetId: concept.id,
    assetStableKey: concept.stableKey,
    // The asset's ACTUAL type — a hardcoded "concept" produced type-
    // mismatched refs for brand/ad/... assets (asset_ref_type_mismatch).
    assetType: concept.type ?? "concept",
    label: concept.name,
    role,
    source,
  };
}

function uniqueMentionAliases(aliases: Array<string | undefined>) {
  const unique: Array<string> = [];
  const seen = new Set<string>();
  aliases.forEach((alias) => {
    const trimmed = (alias ?? "").trim();
    if (!trimmed || seen.has(trimmed.toLowerCase())) {
      return;
    }
    seen.add(trimmed.toLowerCase());
    unique.push(trimmed);
  });
  return unique.sort((left, right) => right.length - left.length);
}


/**
 * Rewrite `@old-name` tokens in surrounding copy (question title / description,
 * and an author-edited matrix row) to `@current-name`. Text with no mention of
 * the asset is returned untouched, which is what lets an author untag a row by
 * deleting its mention.
 */
export function rewriteConceptMentionsInText(
  text: string,
  aliases: Array<string | undefined>,
  nextName: string,
) {
  const trimmedNext = nextName.trim();
  const unique = uniqueMentionAliases(aliases);
  if (!text || !trimmedNext || unique.length === 0) {
    return text;
  }
  const pattern = new RegExp(
    `@\\s*(${unique.map((alias) => assetMentionAliasPattern(alias, unique)).join("|")})${ASSET_MENTION_TRAILING_LOOKAHEAD}`,
    "gi",
  );
  return text.replace(pattern, (match, alias: string, offset: number) => {
    // An author typing "@Ad 2 - Pit Diaper" passes through "@Ad 2" on the way,
    // and "Ad 2" is one of this asset's own aliases. Rewriting that to the full
    // current name mid-word appended the rest of the name to text the author
    // was still typing, one fragment per keystroke. So: when the matched alias
    // is the start of the current name AND the text carries straight on with
    // the rest of it, the mention is already the current name being typed —
    // leave it alone. A real rename (a stale or shortened alias that is not a
    // prefix of the new name) still rewrites.
    const typed = String(alias);
    const lowerNext = trimmedNext.toLowerCase();
    if (lowerNext.startsWith(typed.toLowerCase())) {
      const remainder = trimmedNext.slice(typed.length).toLowerCase();
      const after = text.slice(offset + match.length).toLowerCase();
      // Already the current name, or the author is partway through typing the
      // rest of it (including having typed nothing of it yet).
      if (
        remainder.length === 0 ||
        after.startsWith(remainder) ||
        remainder.startsWith(after)
      ) {
        return match;
      }
    }
    return `@${trimmedNext}`;
  });
}

function shouldRenameChoiceLabel(label: string, previousName: string) {
  const normalizedLabel = label.trim();
  const normalizedPrevious = previousName.trim();
  return (
    normalizedPrevious.length > 0 &&
    (normalizedLabel === normalizedPrevious ||
      normalizedLabel === `@${normalizedPrevious}`)
  );
}

function renameConceptChoiceOption(
  option: ChoiceOption,
  conceptId: string,
  previousName: string,
  nextName: string,
): ChoiceOption {
  const hasBoundConcept = option.assetRefs?.some(
    (assetRef) => assetRef.assetId === conceptId,
  );
  if (
    !hasBoundConcept ||
    !shouldRenameChoiceLabel(option.label, previousName)
  ) {
    return option;
  }
  return { ...option, label: `@${nextName}` };
}

function renameConceptChoiceLabels(
  question: QuestionDefinition,
  conceptId: string,
  previousName: string,
  nextName: string,
): QuestionDefinition {
  if (!nextName.trim()) {
    return question;
  }
  const response = question.response as any;
  if (Array.isArray(response.options)) {
    return {
      ...question,
      response: {
        ...response,
        options: response.options.map((option: ChoiceOption) =>
          renameConceptChoiceOption(option, conceptId, previousName, nextName),
        ),
      },
    };
  }
  if (Array.isArray(response.categories)) {
    return {
      ...question,
      response: {
        ...response,
        categories: response.categories.map((option: ChoiceOption) =>
          renameConceptChoiceOption(option, conceptId, previousName, nextName),
        ),
      },
    };
  }
  return question;
}

/**
 * Keep @mentions in the question stem (and bound choice labels) pointed at the
 * asset's current title after a rename.
 */
export function renameConceptAssetMentions(
  question: QuestionDefinition,
  conceptId: string,
  previousName: string,
  nextName: string,
): QuestionDefinition {
  if (!nextName.trim()) {
    return question;
  }
  const aliases = [
    previousName,
    ...(question.assetRefs ?? [])
      .filter((assetRef) => assetRef.assetId === conceptId)
      .map((assetRef) => assetRef.label),
  ];
  const title = rewriteConceptMentionsInText(
    question.title ?? "",
    aliases,
    nextName,
  );
  const description =
    typeof question.description === "string"
      ? rewriteConceptMentionsInText(question.description, aliases, nextName)
      : question.description;
  const assetRefs = question.assetRefs?.map((assetRef) =>
    assetRef.assetId === conceptId
      ? { ...assetRef, label: nextName.trim() }
      : assetRef,
  );
  return renameConceptChoiceLabels(
    {
      ...question,
      title,
      ...(typeof description === "string" ? { description } : {}),
      ...(assetRefs ? { assetRefs } : {}),
    },
    conceptId,
    previousName,
    nextName,
  );
}

/**
 * Turn a code-generated asset row list into author-owned rows that keep their
 * tags — the moment an author edits one of them.
 *
 * Values stay the asset ids the generated list compiles to, so an edit never
 * moves the answer keys a launched study is already keyed on. Rows arrive
 * tagged (`@Name`), and the author can rename one, reorder them, add plain
 * rows beside them, or untag one by deleting its mention.
 *
 * A question that is not on generated asset rows is returned untouched.
 */
export function withEditableAssetRows(
  question: QuestionDefinition,
  conceptAssets: Array<ConceptAsset>,
): QuestionDefinition {
  const response = question.response as any;
  const rows = response?.rows;
  if (!rows || rows.source !== "tested_assets") {
    return question;
  }
  // Same fallback as `extractRows`: a declared type that matches nothing keeps
  // every asset, so converting never silently empties a grid.
  const bound = conceptAssets.filter(
    (concept) => concept.type === rows.assetType,
  );
  const concepts = bound.length > 0 ? bound : conceptAssets;
  if (concepts.length === 0) {
    return question;
  }
  return {
    ...question,
    response: {
      ...response,
      rows: {
        source: "fixed",
        rows: concepts.map((concept) => ({
          id: `row_${concept.stableKey}`,
          value: concept.id,
          label: `@${concept.name}`,
          assetRefs: [assetRefForConcept(concept, "matrix_row", "mention")],
        })),
      },
    },
  } as QuestionDefinition;
}

export function parseGcsPublicUrl(value: string | null | undefined) {
  const match = (value ?? "").match(
    /^https:\/\/storage\.googleapis\.com\/([^/]+)\/(.+)$/i,
  );
  if (!match) return {};
  return {
    bucket: match[1],
    objectKey: decodeURIComponent(match[2]),
    gcsUri: `gs://${match[1]}/${decodeURIComponent(match[2])}`,
  };
}

/** Media kind from a MIME type: pdf -> document, video/* -> video, else image.
 *  A video stamped 'image' would silently skip analysis and break the twin
 *  stimulus path, so the kind must follow the MIME, never default blindly. */
export function mediaKindForMime(mime: string | undefined): "image" | "document" | "video" {
  const m = (mime || "").toLowerCase();
  if (m === "application/pdf") return "document";
  if (m.startsWith("video/")) return "video";
  return "image";
}

export function mediaRefFromUpload(
  upload: any,
  file: Pick<File, "name" | "type">,
): AssetMediaRef {
  const url =
    upload?.spacesUri || upload?.publicUrl || upload?.filePath || upload?.url;
  const gcs = parseGcsPublicUrl(url);
  if (!gcs.gcsUri) {
    throw new Error("Concept image upload did not return a GCS public URL.");
  }
  return {
    id: `media_${upload?.token || crypto.randomUUID()}`,
    kind: mediaKindForMime(upload?.mimeType || file.type),
    fileId: upload?.token,
    filePath: url,
    publicUrl: url,
    mimeType: upload?.mimeType || file.type,
    altText: file.name,
    label: file.name,
    ...gcs,
  };
}

/** The media ref of a file the backend issued without a browser file (a page capture). */
export function mediaRefFromIssuedFile(issued: IssuedAssetFile): AssetMediaRef {
  return mediaRefFromUpload(issued, {
    name: issued.originalFilename,
    type: issued.mimeType,
  });
}

/** A PDF's page-1 preview the backend issued: the image child of the PDF. */
export function mediaRefFromPdfPreview(
  preview: IssuedAssetFile,
  parentMediaRef: AssetMediaRef,
): AssetMediaRef {
  return {
    ...mediaRefFromIssuedFile(preview),
    id: `${parentMediaRef.id}_slice_0`,
    kind: "image",
    altText: parentMediaRef.altText,
    label: `${parentMediaRef.label || parentMediaRef.altText || "PDF"} preview`,
    sliceOf: parentMediaRef.id,
    sliceIndex: 0,
    sliceRole: "hero",
  };
}
